import { supabase } from "../supabaseClient";
import { enrichOrderTracking } from "../shared/orderTracking";

const ORDERS_STORAGE_KEY = "zeaMaysOrders";

const getOrdersStorageKey = (userId = null) =>
  userId ? `${ORDERS_STORAGE_KEY}:${userId}` : ORDERS_STORAGE_KEY;
const rawApiBaseUrl =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

const resolveApiBaseUrl = () => {
  const normalizedBaseUrl = rawApiBaseUrl.trim().replace(/\/+$/, "");

  if (normalizedBaseUrl) {
    return normalizedBaseUrl;
  }

  if (typeof window !== "undefined") {
    return window.location.origin.replace(/\/+$/, "");
  }

  return "";
};

const apiBaseUrl = resolveApiBaseUrl();

const normalizeOrderItem = (item) => ({
  id: item.id || `${item.order_id || "order"}-${item.product_id || "item"}`,
  order_id: item.order_id || null,
  product_id: item.product_id || null,
  product_slug: item.product_slug || item.productSlug || "",
  product_name: item.product_name || item.name || "",
  selected_size: item.selected_size || item.selectedSize || item.option_label || "",
  unit_price: Number(item.unit_price ?? item.unitPrice ?? item.price) || 0,
  option_price: Number(item.option_price ?? item.optionPrice ?? item.unit_price ?? item.price) || 0,
  option_label: item.option_label || item.optionLabel || item.selected_size || "",
  image: item.image || "",
  price: Number(item.unit_price ?? item.unitPrice ?? item.price) || 0,
  quantity: Number(item.quantity) || 0,
});

const normalizeLegacyCartItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) =>
    normalizeOrderItem({
      product_id: item.id || item.product_id,
      product_name: item.name || item.product_name,
      selected_size: item.selectedSize || item.selected_size || item.optionLabel || "",
      unit_price: item.unitPrice ?? item.price,
      option_price: item.optionPrice ?? item.unitPrice ?? item.price,
      option_label: item.optionLabel || item.option_label || item.selectedSize || "",
      image: item.image || "",
      price: item.unitPrice ?? item.price,
      quantity: item.quantity,
    })
  );
};

const normalizeStoredOrder = (order) => {
  if (!order || typeof order !== "object") {
    return null;
  }

  return enrichOrderTracking({
    id: order.id || order.payment_reference || null,
    user_id: order.user_id || null,
    email: (order.email || "").trim().toLowerCase(),
    total_amount: Number(order.total_amount) || 0,
    status: order.status,
    payment_status: order.payment_status,
    tracking_status: order.status,
    payment_confirmed_at: order.payment_confirmed_at || null,
    status_updated_at: order.status_updated_at || order.created_at || order.createdAt || null,
    status_history: Array.isArray(order.status_history) ? order.status_history : [],
    estimated_delivery_date: order.estimated_delivery_date || null,
    delivery_provider: order.delivery_provider || order.deliveryProvider || "",
    delivery_quote: order.delivery_quote || order.deliveryQuote || {},
    delivery_fee: Number(order.delivery_fee ?? order.deliveryFee) || 0,
    courier: order.courier || "",
    shipment_id: order.shipment_id || "",
    tracking_number: order.tracking_number || "",
    tracking_url: order.tracking_url || "",
    delivery_status: order.delivery_status || "",
    delivery_eta: order.delivery_eta || null,
    quoted_at: order.quoted_at || order.quotedAt || null,
    payment_reference: order.payment_reference ?? null,
    created_at: order.created_at || order.createdAt || new Date().toISOString(),
    order_items: Array.isArray(order.order_items)
      ? order.order_items.map(normalizeOrderItem)
      : normalizeLegacyCartItems(order.cart_items),
  });
};

const readStoredOrders = (userId = null) => {
  try {
    if (userId) {
      const scopedRawOrders = localStorage.getItem(getOrdersStorageKey(userId));
      const scopedOrders = scopedRawOrders ? JSON.parse(scopedRawOrders) : [];

      if (Array.isArray(scopedOrders) && scopedOrders.length) {
        return scopedOrders.map(normalizeStoredOrder).filter(Boolean);
      }

      const legacyRawOrders = localStorage.getItem(ORDERS_STORAGE_KEY);
      const legacyOrders = legacyRawOrders ? JSON.parse(legacyRawOrders) : [];
      const userOrders = Array.isArray(legacyOrders)
        ? legacyOrders.map(normalizeStoredOrder).filter((order) => order?.user_id === userId)
        : [];

      if (userOrders.length) {
        writeStoredOrders(userOrders, userId);
      }

      return userOrders;
    }

    const rawOrders = localStorage.getItem(ORDERS_STORAGE_KEY);
    const parsedOrders = rawOrders ? JSON.parse(rawOrders) : [];

    return Array.isArray(parsedOrders)
      ? parsedOrders.map(normalizeStoredOrder).filter(Boolean)
      : [];
  } catch (error) {
    console.error("Unable to read saved orders:", error);
    return [];
  }
};

const writeStoredOrders = (orders, userId = null) => {
  try {
    localStorage.setItem(getOrdersStorageKey(userId), JSON.stringify(orders));
  } catch (error) {
    console.error("Unable to store orders locally:", error);
  }
};

const cacheOrder = (order) => {
  const normalizedOrder = normalizeStoredOrder(order);

  if (!normalizedOrder) {
    return null;
  }

  const userId = normalizedOrder.user_id || null;
  const existingOrders = readStoredOrders(userId);
  const nextOrders = [normalizedOrder];

  existingOrders.forEach((existingOrder) => {
    const sameReference =
      normalizedOrder.payment_reference &&
      existingOrder.payment_reference === normalizedOrder.payment_reference;
    const sameId = normalizedOrder.id && existingOrder.id === normalizedOrder.id;

    if (!sameReference && !sameId) {
      nextOrders.push(existingOrder);
    }
  });

  writeStoredOrders(nextOrders, userId);
  return normalizedOrder;
};

const getAuthenticatedUser = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    throw new Error(error.message || "Unable to verify signed-in user.");
  }

  if (!user) {
    throw new Error("User not logged in");
  }

  return user;
};

export const getLocalOrders = (userId = null) => readStoredOrders(userId);

export const storeOrderLocally = (order) => cacheOrder(order);

export const loadUserOrders = async (userId) => {
  if (!userId) {
    return [];
  }

  const accessToken = await getAccessToken();
  const endpoint = `${apiBaseUrl}/api/orders?user_id=${encodeURIComponent(userId)}`;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 20000);

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      signal: controller.signal,
    });

    const { payload, rawText } = await parseJsonResponse(response);

    if (!response.ok || payload?.status !== "success" || !Array.isArray(payload?.orders)) {
      console.error("Orders fetch request failed:", {
        endpoint,
        status: response.status,
        payload,
        rawText,
      });

      throw new Error(
        payload?.message ||
          rawText ||
          "Unable to load orders. The payment server returned an invalid response."
      );
    }

    const orders = payload.orders.map(normalizeStoredOrder).filter(Boolean);
    return orders;
  } catch (error) {
    console.error("Orders fetch error:", {
      endpoint,
      error,
    });

    throw new Error(
      buildFetchErrorMessage(error, "Unable to load orders from the payment server.")
    );
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const cancelOrder = async (orderId) => {
  const normalizedOrderId = orderId?.trim();

  if (!normalizedOrderId) {
    throw new Error("Missing order id.");
  }

  const accessToken = await getAccessToken();
  const endpoint = `${apiBaseUrl}/api/orders/${encodeURIComponent(normalizedOrderId)}/cancel`;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 20000);

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      signal: controller.signal,
    });

    const { payload, rawText } = await parseJsonResponse(response);

    if (!response.ok || payload?.status !== "success" || !payload?.order) {
      console.error("Order cancellation request failed:", {
        endpoint,
        status: response.status,
        payload,
        rawText,
      });

      throw new Error(
        payload?.message ||
          rawText ||
          "Unable to cancel this order. The payment server returned an invalid response."
      );
    }

    return storeOrderLocally(payload.order);
  } catch (error) {
    console.error("Order cancellation fetch error:", {
      endpoint,
      error,
    });

    throw new Error(
      buildFetchErrorMessage(error, "Unable to cancel this order on the payment server.")
    );
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const getAccessToken = async () => {
  await getAuthenticatedUser();

  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.access_token) {
    throw new Error(sessionError?.message || "Unable to verify signed-in session.");
  }

  return session.access_token;
};

const parseJsonResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";

  if (!contentType.includes("application/json")) {
    const rawText = await response.text().catch(() => "");

    return {
      payload: null,
      rawText,
    };
  }

  return {
    payload: await response.json().catch(() => null),
    rawText: "",
  };
};

const buildFetchErrorMessage = (error, fallbackMessage) => {
  if (error?.name === "AbortError") {
    return "The payment request timed out. Please try again.";
  }

  if (error instanceof TypeError) {
    return `${fallbackMessage} Check that the payment server is reachable and REACT_APP_API_URL is correct.`;
  }

  return error?.message || fallbackMessage;
};

export const initializeCheckoutPayment = async (checkoutDetails = {}) => {
  const accessToken = await getAccessToken();
  const endpoint = `${apiBaseUrl}/api/payment/initialize`;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 20000);

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        checkoutDetails,
      }),
      signal: controller.signal,
    });

    const { payload, rawText } = await parseJsonResponse(response);

    if (!response.ok || payload?.status !== "success" || !payload?.authorization_url) {
      console.error("Payment initialize request failed:", {
        endpoint,
        status: response.status,
        payload,
        rawText,
      });

      throw new Error(
        payload?.message ||
          rawText ||
          "Unable to initialize payment. The payment server returned an invalid response."
      );
    }

    return payload;
  } catch (error) {
    console.error("Payment initialize fetch error:", {
      endpoint,
      error,
    });

    throw new Error(
      buildFetchErrorMessage(error, "Unable to reach the payment server.")
    );
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const validateCheckoutPromoCode = async (promoCode) => {
  const accessToken = await getAccessToken();
  const endpoint = `${apiBaseUrl}/api/payment/validate-promo`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ promoCode }),
  });
  const { payload, rawText } = await parseJsonResponse(response);
  if (!response.ok || payload?.status !== "success") {
    throw new Error(payload?.message || rawText || "Unable to validate promo code.");
  }
  return payload;
};

export const verifyCheckoutPayment = async (reference) => {
  const normalizedReference = reference?.trim();
  const accessToken = await getAccessToken();

  if (!normalizedReference) {
    throw new Error("Missing payment reference.");
  }

  const endpoint = `${apiBaseUrl}/api/payment/verify/${encodeURIComponent(normalizedReference)}`;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 20000);

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      signal: controller.signal,
    });

    const { payload, rawText } = await parseJsonResponse(response);

    if (!response.ok || payload?.status !== "success" || !payload?.order) {
      console.error("Payment verify request failed:", {
        endpoint,
        status: response.status,
        payload,
        rawText,
      });

      throw new Error(
        payload?.message ||
          rawText ||
          "Payment verification failed. The payment server returned an invalid response."
      );
    }

    return cacheOrder(payload.order);
  } catch (error) {
    console.error("Payment verify fetch error:", {
      endpoint,
      error,
    });

    throw new Error(
      buildFetchErrorMessage(error, "Unable to verify payment with the payment server.")
    );
  } finally {
    window.clearTimeout(timeoutId);
  }
};
